import {
  getEnquiryTypes,
  postEnquiryTypes,
  dropDown,
  getEnquiryExportTypes,
  targetEnquiryTypes,
  updateEnquiryTypes,
  enquiryGrid,
  fileDocumentTypes,
  enquirySurveyTypes,
} from './interfaces'
import { DateFormatter } from '../data/helper'
import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

const dateFormatter = new DateFormatter()

export const getDocuments = async (enquiryId: number): Promise<fileDocumentTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Enquiries/${enquiryId}/Documents`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data: fileDocumentTypes[] = response.data

  return data
}

export const getEnquirySurvey = async (enquiryId: number): Promise<enquirySurveyTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<enquirySurveyTypes>(
    `https://${apiURL}/Enquiries/${enquiryId}/Survey`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return response.data
}

export const updateEnquirySurvey = async (
  enquiryId: number,
  surveyNotes: string
): Promise<enquirySurveyTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.put<enquirySurveyTypes>(
    `https://${apiURL}/Enquiries/${enquiryId}/Survey`,
    { surveyNotes },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return response.data
}

export const getEnquiries = async (showDead: boolean, showProgressed: boolean): Promise<enquiryGrid[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<enquiryGrid[]>(
    `https://${apiURL}/Enquiries?ShowDead=${showDead}&ShowProgressed=${showProgressed}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data = response.data

  const enquiryData: enquiryGrid[] = (data as enquiryGrid[]).map(
    (item: enquiryGrid) => ({
      isUnassignedReferral: item.isUnassignedReferral,
      noteCount: item.noteCount,
      enquiryId: item.enquiryId,
      ref: item.ref,
      refFormatted: item.refFormatted,
      description: item.description,      
      clientId: item.clientId,
      clientName: item.clientName,
      siteId: item.siteId,
      siteAddr: item.siteAddr,
      employeeCreatedById: item.employeeCreatedById,
      employeeCreatedByName: item.employeeCreatedByName,
      employeeAssignedToId: item.employeeAssignedToId,
      employeeAssignedToName: item.employeeAssignedToName,
      createdDate: dateFormatter.format(item.createdDate),
      minsSinceCreation: item.minsSinceCreation,
      deadDate: dateFormatter.format(item.deadDate),
      progressed: item.progressed,
      dueDate: item.dueDate ? dateFormatter.format(item.dueDate) : null,
      priorityId: item.priorityId,
      priority: item.priority,
      highPriority: item.highPriority,
      note1Details: item.note1Details,
      note2Details: item.note2Details,
      note3Details: item.note3Details,
      note1Completed: item.note1Completed,
      note2Completed: item.note2Completed,
      note3Completed: item.note3Completed,
    })
  )

  return enquiryData;
}

export const getEnquiriesWithPagination = async (params: {
  skip: number
  take: number
  showDead: boolean
  showProgressed: boolean
  sort: any[]
  filter: any
}): Promise<{ data: enquiryGrid[], total: number }> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  // Build query string parameters
  const queryParams = new URLSearchParams({
    skip: params.skip.toString(),
    take: params.take.toString(),
    ShowDead: params.showDead.toString(),
    ShowProgressed: params.showProgressed.toString(),
  })

  // Add sorting parameters if present
  if (params.sort && params.sort.length > 0) {
    // Use only the first sort item (primary sort)
    const primarySort = params.sort[0]
    queryParams.append('sortField', primarySort.field)
    queryParams.append('sortDir', primarySort.dir)
  }

  // Add filter parameters if present
  if (params.filter) {
    queryParams.append('filter', JSON.stringify(params.filter))
  }

  const url = `https://${apiURL}/Enquiries/paginated?${queryParams.toString()}`
  console.log('API URL:', url)
  console.log('Params:', { skip: params.skip, take: params.take, sort: params.sort, filter: params.filter })

  const response = await axios.get<{ data: enquiryGrid[], total: number }>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  // Transform the data to match our interface
  const enquiryData: enquiryGrid[] = response.data.data.map((item) => ({
    ...item,
    createdDate: dateFormatter.format(item.createdDate),
    deadDate: item.deadDate ? dateFormatter.format(item.deadDate) : '',
    dueDate: item.dueDate ? dateFormatter.format(item.dueDate) : null,
  }))

  return {
    data: enquiryData,
    total: response.data.total
  }
}

export const getEnquiriesForEnquiriesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  const enquiryDropdownData: dropDown[] = (data as dropDown[]).map((item: any) => ({
    text: item.text,
    value: item.value,
  }))

  return enquiryDropdownData
}

export const getClientsForEnquiriesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/Clients/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getSitesForEnquiriesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/Sites/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getCreatedByForEnquiriesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/CreatedBy/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getAssignedToForEnquiriesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/AssignedTo/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getEnquiriesForExport = async (showDead: boolean, showProgressed: boolean): Promise<getEnquiryExportTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<enquiryGrid[]>(
    `https://${apiURL}/Enquiries?ShowDead=${showDead}&ShowProgressed=${showProgressed}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data = response.data

  const enquiryExportData: getEnquiryExportTypes[] = (data as enquiryGrid[]).map(
    (item: enquiryGrid) => ({
      enquiryId: item.enquiryId,
      ref: item.ref,
      refFormatted: item.refFormatted,
      description: item.description,
      minsSinceCreation: item.minsSinceCreation,
      clientName: item.clientName,
      siteAddress: item.siteAddr,
      employeeCreatedby: item.employeeCreatedByName,
      employeeAssignedTo: item.employeeAssignedToName,
      createdDate: dateFormatter.format(item.createdDate),
      note1Details: item.note1Details,
      note2Details: item.note2Details,
      note3Details: item.note3Details,
      note1Completed: item.note1Completed,
      note2Completed: item.note2Completed,
      note3Completed: item.note3Completed,
    })
  )

  return enquiryExportData
}

export const submitEnquiry = async (enquiry: postEnquiryTypes) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  // Ensure dueDate is sent in YYYY-MM-DD format without timezone conversion
  let dueDateISO = null;
  if (enquiry.dueDate) {
    let dateObj;
    if (typeof enquiry.dueDate === 'string' || typeof enquiry.dueDate === 'number') {
      dateObj = new Date(enquiry.dueDate);
    } else if (typeof enquiry.dueDate === 'object' && enquiry.dueDate !== null && typeof (enquiry.dueDate as any).getTime === 'function') {
      dateObj = enquiry.dueDate as Date;
    } else {
      dateObj = null;
    }
    
    if (dateObj && !isNaN(dateObj.getTime())) {
      // Use local date formatting to avoid timezone issues
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      dueDateISO = `${year}-${month}-${day}`;
    }
  }
  console.log('Submitting enquiry with dueDate:', dueDateISO);
  const response = await axios({
    url: `https://${apiURL}/Enquiries`,
    method: 'post',
    data: {
      contactId: enquiry.contactId,
      clientName: enquiry.clientName,
      clientId: enquiry.clientId,
      siteId: enquiry.siteId,
      priorityId: enquiry.priorityId,
      enquirysourceId: enquiry.enquirysourceId ? enquiry.enquirysourceId : 0,
      description: enquiry.description,
      employeeAssignedToId: enquiry.employeeAssignedToId,
      dueDate: dueDateISO,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: postEnquiryTypes = response.data
  return data
}

export const getEnquiryDropdown = async (): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/ForDropdown`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  const enquiryDropdownData: dropDown[] = (data as dropDown[]).map(
    (item: any) => ({
      value: item.value,
      text: item.text,
    })
  )

  return enquiryDropdownData
}

export const getEnquiryCount = async (): Promise<number> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/enquiries/myenquiries`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  const enquiryData: dropDown[] = (data as dropDown[]).map((item: any) => ({
    value: item.value,
    text: item.text,
  }))

  const enquiryCount: number = enquiryData.length

  return enquiryCount
}

export const getTargetEnquiry = async (
  enquiryId: number
): Promise<targetEnquiryTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Enquiries/${enquiryId}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: targetEnquiryTypes = response.data
  const targetEnquiryData: targetEnquiryTypes = {
    // Carried through explicitly: this builder reshapes the response field
    // by field, so anything not named here is dropped before it reaches the form.
    workingsAdjustmentPercent: data.workingsAdjustmentPercent ?? null,
    contact: {
      contactId: data.contact?.contactId || 0,
      title: data.contact?.title,
      firstname: data.contact?.firstname,
      lastname: data.contact?.lastname,
      phone: data.contact?.phone,
      email: data.contact?.email,
    } ,
    jobId: data.jobId,
    quoteId: data.quoteId,
    jobRef: data.jobRef,
    quoteRef: data.quoteRef,
    enquiryId: data.enquiryId,
    ref: data.ref,
    refFormatted: data.refFormatted,
    description: data.description,
    client: {
      clientId: data.client?.clientId,
      clientName: data.client?.clientName,
      accountRef: data.client?.accountRef,
      contactName: data.client?.contactName,
      phone:
        data.client && data.client.phone
          ? data.client.phone
          : 'No phone number',
      email:
        data.client && data.client.email
          ? data.client.email
          : 'No email address',
    },
    clientName: data.clientName,
    site: {
      siteId: data.site.siteId,
      siteAddr: data.site.siteAddr,
      postcode: data.site.postcode,
    },
    priority: {
      priorityId:
        data.priority && data.priority.priorityId
          ? data.priority.priorityId
          : null,
      description:
        data.priority && data.priority.description
          ? data.priority.description
          : 'No priority set',
    },
    enquirysource: {
      enquirysourceId:
        data.enquirysource && data.enquirysource.enquirysourceId
          ? data.enquirysource.enquirysourceId
          : null,
      description:
        data.enquirysource && data.enquirysource.description
          ? data.enquirysource.description
          : 'No enquiry source set',
    },
    employeeCreatedby: {
      employeeId:
        data.employeeCreatedby && data.employeeCreatedby.employeeId
          ? data.employeeCreatedby.employeeId
          : null,
      employeeName:
        data.employeeCreatedby && data.employeeCreatedby.employeeName
          ? data.employeeCreatedby.employeeName
          : 'No employee name',
      position:
        data.employeeCreatedby && data.employeeCreatedby.position
          ? data.employeeCreatedby.position
          : 'No position',
    },
    employeeAssignedTo: {
      employeeId:
        data.employeeAssignedTo && data.employeeAssignedTo.employeeId
          ? data.employeeAssignedTo.employeeId
          : null,
      employeeName:
        data.employeeAssignedTo && data.employeeAssignedTo.employeeName
          ? data.employeeAssignedTo.employeeName
          : 'No employee name',
      position:
        data.employeeAssignedTo && data.employeeAssignedTo.position
          ? data.employeeAssignedTo.position
          : 'No position',
    },
    projectFolderUrl: data.projectFolderUrl,
    deadDate: data.deadDate || null,
    dueDate: data.dueDate,
  }
  return targetEnquiryData
}

export const updateEnquiry = async (
  enquiry: updateEnquiryTypes,
  enquiryId: number
) => {
  const token = localStorage.getItem('authToken')

  console.log('>> HTTP PUT /Enquiries/' + enquiryId + '  payload:', enquiry)

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Enquiries/${enquiryId}`,
    method: 'put',
    data: {
      enquiryId: enquiryId,
      clientId: enquiry.clientId || 0,
      siteId: enquiry.siteId || 0,
      contactId: enquiry.contactId || 0,
      priorityId: enquiry.priorityId,
      enquirysourceId: enquiry.enquirysourceId,
      description: enquiry.description,
      employeeAssignedToId: enquiry.employeeAssignedToId,
      projectFolderUrl: enquiry.projectFolderUrl,
      deadDate: enquiry.deadDate || null,
      workingsAdjustmentPercent: enquiry.workingsAdjustmentPercent ?? null,
      // Ensure dueDate is sent in YYYY-MM-DD format without timezone conversion
      dueDate: (() => {
        console.log('updateEnquiry - Raw dueDate:', enquiry.dueDate);
        if (!enquiry.dueDate) {
          console.log('updateEnquiry - No dueDate provided, returning null');
          return null;
        }
        let dateObj;
        if (typeof enquiry.dueDate === 'string' || typeof enquiry.dueDate === 'number') {
          dateObj = new Date(enquiry.dueDate);
        } else if (typeof enquiry.dueDate === 'object' && enquiry.dueDate !== null && typeof (enquiry.dueDate as Date).getTime === 'function') {
          dateObj = enquiry.dueDate as Date;
        } else {
          dateObj = null;
        }
        
        if (dateObj && !isNaN(dateObj.getTime())) {
          // Use local date formatting to avoid timezone issues
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;
          console.log('updateEnquiry - Formatted dueDate:', formattedDate);
          return formattedDate;
        }
        console.log('updateEnquiry - Invalid date, returning null');
        return null;
      })()
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })
  const data: postEnquiryTypes = response.data
  return data
}

export const deleteEnquiry = async (enquiryId: number) => {
  const token = localStorage.getItem('authToken')

  console.log(token)

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Enquiries/MarkAsDead/${enquiryId}`,
    method: 'put',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data
}
